/**
 * Workspace Tabs — Vuex module
 *
 * Tracks open tabs for the multi-tab workspace feature.
 * Each tab represents a MODULE (e.g. /erp/ledgers), not individual pages.
 * Sub-routes (create, edit/:id) update the same tab instead of opening new ones.
 * Only navigating to a different module opens a new tab.
 */

import {
  getModuleKey as _getModuleKey,
  pushTabHistory,
  moveTabIdx,
  setTabIdx,
} from './tabsHistoryLogic.mjs';

const STORAGE_KEY = 'workspace:tabs';
const ENABLED_KEY = 'workspace:tabs:enabled';
const MAX_TABS = 10;

// Re-exported so existing imports (router/index.js) keep working.
export const getModuleKey = _getModuleKey;

function loadFromSession() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function saveToSession(openTabs, activeKey) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ openTabs, activeKey }));
  } catch {
    // sessionStorage full or unavailable — silently ignore
  }
}

const saved = loadFromSession();

export const state = {
  enabled: localStorage.getItem(ENABLED_KEY) !== 'false', // default true
  openTabs: saved?.openTabs || [],
  activeKey: saved?.activeKey || null,
};

export const getters = {
  aliveComponentNames(state) {
    return state.openTabs.map((t) => t.componentName).filter(Boolean);
  },
  tabByKey: (state) => (key) => state.openTabs.find((t) => t.key === key),
  tabCount(state) {
    return state.openTabs.length;
  },
  isEnabled(state) {
    return state.enabled;
  },
};

export const mutations = {
  ADD_TAB(state, tab) {
    state.openTabs.push(tab);
  },
  REMOVE_TAB(state, key) {
    state.openTabs = state.openTabs.filter((t) => t.key !== key);
  },
  SET_ACTIVE(state, key) {
    state.activeKey = key;
  },
  TOUCH_TAB(state, key) {
    const tab = state.openTabs.find((t) => t.key === key);
    if (tab) tab.openedAt = Date.now();
  },
  UPDATE_TAB_PATH(state, { key, path, title }) {
    const tab = state.openTabs.find((t) => t.key === key);
    if (tab) {
      tab.path = path;
      if (title) tab.title = title;
    }
  },
  PUSH_TAB_HISTORY(state, { key, path }) {
    const tab = state.openTabs.find((t) => t.key === key);
    if (tab) pushTabHistory(tab, path);
  },
  MOVE_TAB_IDX(state, { key, delta }) {
    const tab = state.openTabs.find((t) => t.key === key);
    if (tab) moveTabIdx(tab, delta);
  },
  SET_TAB_IDX(state, { key, idx }) {
    const tab = state.openTabs.find((t) => t.key === key);
    if (tab) setTabIdx(tab, idx);
  },
  PIN_TAB(state, key) {
    const tab = state.openTabs.find((t) => t.key === key);
    if (tab) tab.pinned = !tab.pinned;
  },
  SET_ENABLED(state, enabled) {
    state.enabled = enabled;
    localStorage.setItem(ENABLED_KEY, String(enabled));
  },
  CLEAR_TABS(state) {
    state.openTabs = [];
    state.activeKey = null;
  },
  SET_TABS(state, { openTabs, activeKey }) {
    state.openTabs = openTabs;
    state.activeKey = activeKey;
  },
};

export const actions = {
  /**
   * Open or update a tab. Key is the module base path.
   * If a tab with this key exists, update its current path and activate it.
   * Otherwise, create a new tab.
   */
  openTab({ state, commit, dispatch }, { key, path, title, icon, componentName, recordHistory = true }) {
    const existing = state.openTabs.find((t) => t.key === key);
    if (existing) {
      commit('SET_ACTIVE', key);
      commit('TOUCH_TAB', key);
      // Update the tab's current path so clicking it navigates to the latest page
      commit('UPDATE_TAB_PATH', { key, path, title });
      if (recordHistory) commit('PUSH_TAB_HISTORY', { key, path });
    } else {
      // Evict if at capacity
      if (state.openTabs.length >= MAX_TABS) {
        dispatch('evictOldest');
      }
      commit('ADD_TAB', {
        key,
        path,
        title: title || 'Untitled',
        icon: icon || 'ri-pages-line',
        componentName: componentName || null,
        pinned: false,
        openedAt: Date.now(),
        history: [path],
        idx: 0,
      });
      commit('SET_ACTIVE', key);
    }
    saveToSession(state.openTabs, state.activeKey);
  },

  evictOldest({ state, commit }) {
    const unpinned = state.openTabs
      .filter((t) => !t.pinned)
      .sort((a, b) => a.openedAt - b.openedAt);
    if (unpinned.length > 0) {
      commit('REMOVE_TAB', unpinned[0].key);
    }
  },

  closeTab({ state, commit }, key) {
    const idx = state.openTabs.findIndex((t) => t.key === key);
    if (idx === -1) return null;

    commit('REMOVE_TAB', key);

    // Determine which tab to activate next
    let nextKey = null;
    if (state.activeKey === key && state.openTabs.length > 0) {
      const nextIdx = Math.min(idx, state.openTabs.length - 1);
      nextKey = state.openTabs[nextIdx].key;
      commit('SET_ACTIVE', nextKey);
    } else if (state.openTabs.length === 0) {
      commit('SET_ACTIVE', null);
    }

    saveToSession(state.openTabs, state.activeKey);
    return nextKey;
  },

  activateTab({ state, commit }, key) {
    const tab = state.openTabs.find((t) => t.key === key);
    if (!tab) return null;
    commit('SET_ACTIVE', key);
    commit('TOUCH_TAB', key);
    saveToSession(state.openTabs, state.activeKey);
    return tab.path;
  },

  pinTab({ commit, state }, key) {
    commit('PIN_TAB', key);
    saveToSession(state.openTabs, state.activeKey);
  },

  toggleEnabled({ state, commit }) {
    const newVal = !state.enabled;
    commit('SET_ENABLED', newVal);
    if (!newVal) {
      commit('CLEAR_TABS');
      sessionStorage.removeItem(STORAGE_KEY);
    }
  },

  clearAll({ commit }) {
    commit('CLEAR_TABS');
    sessionStorage.removeItem(STORAGE_KEY);
  },
};
